// @flow
import React, { useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import BackgroundIcon from '../../Icons/BackgroundIcon';
import bgCareEveryDay from './bg-care-every-day.png';
import bgCity from './bg-city.png';
import bgHomey from './bg-homey.png';
import bgLove from './bg-love.png';
import bgYouGotThis from './bg-you-got-this.png';
import styles from './styles.module.scss';

export type Background =
  | { type: 'none' }
  | { type: 'blur', amount: number }
  | { type: 'color', value: string }
  | { type: 'image', url: string, label: string };

export const BACKGROUNDS: Background[] = [
  { type: 'none' },
  { type: 'blur', amount: 14 },
  { type: 'color', value: '#FFD400' },
  { type: 'color', value: '#E8E1CF' },
  { type: 'color', value: '#E6EBEC' },
  { type: 'color', value: '#1460F8' },
  { type: 'image', url: bgLove, label: 'Love' },
  { type: 'image', url: bgCareEveryDay, label: 'Care Every Day' },
  { type: 'image', url: bgYouGotThis, label: 'You Got This' },
  { type: 'image', url: bgCity, label: 'City' },
  { type: 'image', url: bgHomey, label: 'Homey' },
];

type Props = {
  selected: Background,
  onChange: (bg: Background) => void,
};

const isSameBackground = (a: Background, b: Background) => {
  if (a.type !== b.type) return false;
  if (a.type === 'color' && b.type === 'color') return a.value === b.value;
  if (a.type === 'image' && b.type === 'image') return a.url === b.url;
  return true;
};

// Produces a stable, meaningful key for each background entry so React can
// efficiently reconcile the list without using array indices.
const getBgKey = (bg: Background): string => {
  if (bg.type === 'color') return `color-${bg.value}`;
  if (bg.type === 'image') return `image-${bg.url}`;
  return bg.type;
};

const getBgAriaLabel = (bg: Background, formatMessage: Function): string => {
  if (bg.type === 'none') {
    return formatMessage({ id: 'app.video-back.background.none' });
  }
  if (bg.type === 'blur') {
    return formatMessage({ id: 'app.video-back.background.blur' });
  }
  if (bg.type === 'image') {
    return bg.label;
  }
  return formatMessage({ id: 'app.video-back.background.color' }, { color: bg.value });
};

const BackgroundSelector = ({ selected, onChange }: Props) => {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (bg: Background) => {
    onChange(bg);
    setIsOpen(false);
  };

  const isActiveBackground = selected.type !== 'none';

  const handleBlurAmount = (e: SyntheticInputEvent<HTMLInputElement>) => {
    if (selected.type === 'blur') {
      onChange({ type: 'blur', amount: Number(e.target.value) });
    }
  };

  return (
    <div className={styles.root}>
      {/* Toggle button — always visible, small footprint in the corner */}
      <button
        type="button"
        aria-label={formatMessage({ id: 'app.video-back.background.toggle' })}
        aria-expanded={isOpen}
        className={classNames(styles.toggleButton, { [styles.toggleButtonActive]: isActiveBackground })}
        onClick={() => setIsOpen((o) => !o)}
      >
        <BackgroundIcon />
      </button>

      {/* Expanded panel — appears below the toggle */}
      {isOpen && (
        <div className={styles.panel}>
          <span className={styles.label}>{formatMessage({ id: 'app.video-back.background.label' })}</span>
          <div className={styles.options}>
            {BACKGROUNDS.map((bg) => {
              const isSelected = isSameBackground(bg, selected);
              return (
                <button
                  key={getBgKey(bg)}
                  type="button"
                  aria-pressed={isSelected}
                  aria-label={getBgAriaLabel(bg, formatMessage)}
                  className={classNames(styles.option, { [styles.optionSelected]: isSelected })}
                  onClick={() => handleSelect(bg)}
                >
                  {bg.type === 'none' && <span className={styles.noneIcon}>✕</span>}
                  {bg.type === 'blur' && <span className={styles.blurPreview} />}
                  {bg.type === 'color' && <span className={styles.colorSwatch} style={{ backgroundColor: bg.value }} />}
                  {bg.type === 'image' && <img src={bg.url} alt={bg.label} className={styles.imageThumbnail} />}
                </button>
              );
            })}
          </div>

          {/* Blur amount slider — only shown while blur is the active background */}
          {selected.type === 'blur' && (
            <div className={styles.blurSliderRow}>
              <span className={styles.blurSliderLabel}>○</span>
              <input
                type="range"
                min="4"
                max="28"
                step="2"
                value={selected.amount}
                onChange={handleBlurAmount}
                className={styles.blurSlider}
                aria-label="Blur amount"
              />
              <span className={styles.blurSliderLabel}>◉</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BackgroundSelector;
